import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ColDef, ColumnResizedEvent } from 'ag-grid-community';
import { ActionsLinkComponent } from '../../../shared/actions-link/actions-link.component';
import { currencyComparator } from '../../../create-request/components/transactions-table/table-helpers';
import { agFilterNames } from '../../../shared/types/AgFilterNames';
import { Observable, Subject, takeUntil } from 'rxjs';
import { TableComponentState } from '../../../shared/types/TableComponentState';
import { Store } from '@ngrx/store';
import { selectCreateRuleTableState, selectCreateCustomRuleSectionRulesList, selectCallCustomRulesGetFailed } from '../../reducers';
import { InitReleaseCreateRuleModalComponent } from '../init-release-create-rule-modal/init-release-create-rule-modal.component';
import { InitReleaseDeleteRuleModalComponent } from '../init-release-delete-rule-modal/init-release-delete-rule-modal.component';
import { InitReleaseActions } from '../../actions';
import { CustomRuleListTableColumn, CustomRuleGetResponseData, TableValuesFormatDisplay } from '../../Models/CreateCustomRule/CreateCustomRule';
import { formatCurrency } from '@angular/common';
import { Constants } from '../../../shared/utilities/Constants';

@Component({
  standalone: false,
    selector: 'mm-init-release-create-rule-section',
    templateUrl: './init-release-create-rule-section.component.html',
    styleUrls: ['./init-release-create-rule-section.component.scss']
})
export class InitReleaseCreateRuleSectionComponent implements OnInit, OnDestroy {

    destroy$: Subject<boolean> = new Subject<boolean>();
    createRuleTableState$: Observable<TableComponentState>;
    showErrorForGetRules: boolean = false;

    ellipsisTooltipValueGetter = (params: any) => {
    if (!params.node || !params.column || !params.api) return null;
    // Get cell renderer instance for this cell
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.font = getComputedStyle(document.body).font;
    const cellTextWidth = context.measureText(params.value ?? '').width;
    const currentColWidth = params.column.getActualWidth();
    if (cellTextWidth > currentColWidth - 3) {
      return params.value ?? '';
    }
    return null;
  };
 
    colNames = {
        actions: 'Actions',
        ruleName: 'Rule Name',
        transactionTypes: 'Transaction Types',
        party: 'Party',
        authorization: 'Authorization',
        amount: 'Amount',
        dateCreated: 'Date Created',
        id: 'ID',
        ruleType: 'RuleType'
    };

    rowData$: Observable<CustomRuleListTableColumn[]>;

    columnDefs: ColDef[] = [];
    defColumnDefs: ColDef[] = [
        {
            field: 'actions',
            headerName: this.colNames.actions,
            filter: false,
            suppressMovable: true,
            sortable: false,
            cellRenderer: ActionsLinkComponent,
            cellRendererParams: {
              onClick: this.deleteLinkClicked.bind(this),
              data: {
                isDraft: true,
              },
              delete: 'Delete',
              tableName: 'createRuleTable',
            },
            width: 80,
            minWidth: 80,
            maxWidth: 80,
            resizable: false,
            type: 'centerAligned',
            tooltipValueGetter: (params) => this.ellipsisTooltipValueGetter(params)
        },  
        {
            field: 'ruleName',
            headerName: this.colNames.ruleName,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            flex: 1,
            resizable: true,
            cellClass: 'ag-ellipsis-cell',
            tooltipValueGetter: (params) => this.ellipsisTooltipValueGetter(params)
        },  
        {
            field: 'transactionTypes',
            headerName: this.colNames.transactionTypes,
            filter: agFilterNames.set,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            flex: 1,
            resizable: true,
            cellClass: 'ag-ellipsis-cell',
            tooltipValueGetter: (params) => this.ellipsisTooltipValueGetter(params)
        },  
        {
            field: 'party',
            headerName: this.colNames.party,
            filter: agFilterNames.set,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            flex: 1,
            resizable: true,
            cellClass: 'ag-ellipsis-cell',
            tooltipValueGetter: (params) => this.ellipsisTooltipValueGetter(params)
        },  
        {
            field: 'authorization',
            headerName: this.colNames.authorization,
            filter: agFilterNames.set,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            flex: 1,
            resizable: true,
            cellClass: 'ag-ellipsis-cell',
            tooltipValueGetter: (params) => this.ellipsisTooltipValueGetter(params)
        },  
        {
            field: 'amount',
            headerName: this.colNames.amount,
            comparator: currencyComparator,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            filterValueGetter: params => {
              if (params.data.amount === 'N/A') {
                return 'N/A';
              }
              return params.data.amount != null ? `Greater than ${formatCurrency(Number(params.data.amount), 'en-US', '$', 'USD').toLowerCase()}` : '';
            },
            flex: 1,
            resizable: true,
            valueFormatter: params => {
              if (params.value === 'N/A') {
                return params.value;
              }
              return params.value != null ? `Greater than ${formatCurrency(Number(params.value), 'en-US', '$', 'USD')}` : '';
            },
            cellClass: 'ag-ellipsis-cell',
            tooltipValueGetter: (params) => this.ellipsisTooltipValueGetter(params)
        },  
        {
            field: 'dateCreated',
            headerName: this.colNames.dateCreated,
            comparator: currencyComparator,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            type: 'rightAligned',
            flex: 1,
            resizable: true,
            hide: true,
            cellClass: 'ag-ellipsis-cell',
            tooltipValueGetter: (params) => this.ellipsisTooltipValueGetter(params)
        },  
        {
            field: 'id',
            headerName: this.colNames.id,
            comparator: currencyComparator,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            type: 'rightAligned',
            flex: 1,
            resizable: true,
            hide: true,
            cellClass: 'ag-ellipsis-cell',
            tooltipValueGetter: (params) => this.ellipsisTooltipValueGetter(params)
        },  
        {
            field: 'ruleType',
            headerName: this.colNames.ruleType,
            comparator: currencyComparator,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            type: 'rightAligned',
            flex: 1,
            resizable: true,
            hide: true,
            cellClass: 'ag-ellipsis-cell',
            tooltipValueGetter: (params) => this.ellipsisTooltipValueGetter(params)
        }
    ];

    constructor(private store: Store) { }

    @ViewChild(InitReleaseCreateRuleModalComponent) createRuleModal!: InitReleaseCreateRuleModalComponent;
    @ViewChild(InitReleaseDeleteRuleModalComponent) deleteRuleModal!: InitReleaseDeleteRuleModalComponent;

    ngOnInit() {
      this.columnDefs = this.defColumnDefs;
      this.store.dispatch(InitReleaseActions.callCreateCustomRulesGet());
      this.createRuleTableState$ = this.store.select(selectCreateRuleTableState);
      this.store.select(selectCreateCustomRuleSectionRulesList).pipe(takeUntil(this.destroy$)).subscribe(getData => {
        let data = this.mapRuleListdataToRowData(getData);
        // Format the columns with predefined values
        this.formatColumns(data);
        this.rowData$ = new Observable(observer => {
          observer.next(data);
          observer.complete();
        });
      });
      this.store.select(selectCallCustomRulesGetFailed).pipe(takeUntil(this.destroy$)).subscribe(error => {
        this.showErrorForGetRules = error;
      });
    }

    mapRuleListdataToRowData(rules: CustomRuleGetResponseData[]): CustomRuleListTableColumn[] {
      if (!Array.isArray(rules) || rules.length === 0) {
      this.store.dispatch(InitReleaseActions.hasEmptyResponse());
      return [];
    }
      // Sort by dateCreated latest first
      const sortedRules = [...rules].sort((a, b) => {
        const dateA = new Date(a.attributes?.dateCreated || '').getTime();
        const dateB = new Date(b.attributes?.dateCreated || '').getTime();
        return dateB - dateA;
      });
      return sortedRules.map(rule => ({
        actions: '',
        ruleName: rule.attributes?.contextName || '',
        transactionTypes: Array.isArray(rule.attributes?.transactionTypes) && rule.attributes.transactionTypes.length > 0
          ? rule.attributes.transactionTypes
          : ['All'],
        party: Array.isArray(rule.attributes?.party) && rule.attributes.party.length > 0
          ? rule.attributes.party
          : ['All'],
        authorization: Array.isArray(rule.attributes?.authorization) && rule.attributes.authorization.length > 0
          ? rule.attributes.authorization
          : ['All'],
        amount: rule.attributes?.threshold != null ? rule.attributes.threshold : 0,
        id: rule.id ?? '', // top-level id
        dateCreated: rule.attributes?.dateCreated || '',
        ruleType: rule.attributes?.ruleType || ''
      }));
    }

    onCreateRuleClicked() {
      this.createRuleModal.onCreateRuleButtonClickedEvent();
    }

    deleteLinkClicked(event: any) {
        let selectedRuleId = event.rowData['id'];
        this.deleteRuleModal.onDeleteLinkClickedEvent(selectedRuleId);
    }

    ngOnDestroy(): void {
    }

    get serviceErrorMessage(): string {
      return Constants.genericServiceError;
    }

    formatColumns(rowData: CustomRuleListTableColumn[]): void {
      const allTransactionTypes = ['ach', 'wire', 'check', 'internal-transfer'].sort();
      const allParties = ['1st-party', '3rd-party'].sort();
      const allAuthorizations = ['no-auth-required', 'one-time-client-auth', 'standing-auth'].sort();
      rowData.forEach(row => {
        if (row.ruleType === 'new-standing-authorizations') {
          row.transactionTypes = 'N/A';
          row.authorization = 'N/A';
          row.amount = 'N/A';
          this.formatMultiSelectColumn(row, 'party', allParties);
        } else {
          this.formatMultiSelectColumn(row, 'transactionTypes', allTransactionTypes);
          this.formatMultiSelectColumn(row, 'party', allParties);
          this.formatMultiSelectColumn(row, 'authorization', allAuthorizations);
        }
      });
    }

    private formatMultiSelectColumn(row: { [key: string]: any }, key: string, allValues: string[]): void {
      let selected = row[key];
      if (Array.isArray(selected)) {
        const sorted = selected.slice().sort();
        if (
          (sorted.length === allValues.length &&
            sorted.every((val, i) => val === allValues[i])) ||
          (sorted.length === 0)
        ) {
          row[key] = 'All';
        } else {
            row[key] = sorted.sort((a, b) => a.localeCompare(b)).join(', ');
          if (key == 'transactionTypes' || key == 'authorization' || key == 'party') {
            row[key] = this.formatValues(row[key]);
          }
        }
      }
    }

    private formatValues(value: string): string {
      return value
        .split(',')
        .map(s => s.trim())
        .map(t => (TableValuesFormatDisplay[t as keyof typeof TableValuesFormatDisplay] ?? (t.charAt(0).toUpperCase() + t.slice(1))))
        .join(', ');
    }

}
